// Serviço da Certidão Nada Consta (API de Certidão Nada Consta):
//   - emitir():   POST /certidoes/nada-consta/emitir (JWT, sem corpo)
//   - baixarPdf(): GET /certidoes/nada-consta/pdf (JWT, devolve o PDF)
//   - validar():  GET /certidoes/nada-consta/validar/:codigo (pública)
//
// Todos devolvem { success, data } ou { success: false, code, error, data },
// no mesmo padrão dos outros services do portal — a tela decide o que mostrar
// a partir do `code` da API.

import certidaoApi, { certidaoPublicaApi } from '../../../services/certidaoApi';
import logger from '../../../utils/logger';

export const CODIGOS_ERRO_CERTIDAO = {
  NAO_EMITIVEL: 'CERTIDAO_NAO_EMITIVEL',
  EMISSAO_EM_ANDAMENTO: 'EMISSAO_EM_ANDAMENTO',
  INTEGRATION_ERROR: 'INTEGRATION_ERROR',
  EMISSAO_NAO_CONFIGURADA: 'EMISSAO_NAO_CONFIGURADA',
  NAO_ENCONTRADA: 'CERTIDAO_NAO_ENCONTRADA',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  RATE_LIMIT: 'RATE_LIMIT'
};

// Mensagens próprias do portal pros erros conhecidos — a `message` da API
// fica como fallback pros que não estão aqui.
const MENSAGENS_ERRO = {
  [CODIGOS_ERRO_CERTIDAO.EMISSAO_EM_ANDAMENTO]: 'Já existe uma emissão em andamento. Aguarde alguns segundos e tente novamente.',
  [CODIGOS_ERRO_CERTIDAO.INTEGRATION_ERROR]: 'Não foi possível concluir a verificação no momento. Tente novamente em alguns minutos.',
  [CODIGOS_ERRO_CERTIDAO.EMISSAO_NAO_CONFIGURADA]: 'A emissão da certidão está temporariamente indisponível. Tente novamente mais tarde.',
  [CODIGOS_ERRO_CERTIDAO.NAO_ENCONTRADA]: 'Nenhuma certidão foi encontrada com este código.',
  [CODIGOS_ERRO_CERTIDAO.VALIDATION_ERROR]: 'O código informado não está no formato de uma certidão válida.',
  [CODIGOS_ERRO_CERTIDAO.RATE_LIMIT]: 'Muitas consultas em pouco tempo. Aguarde alguns minutos e tente novamente.'
};

const NOME_PDF_PADRAO = 'certidao-nada-consta.pdf';

const montarErro = (error, mensagemPadrao) => {
  const status = error.response?.status;
  const body = error.response?.data || {};
  const code = status === 429 ? CODIGOS_ERRO_CERTIDAO.RATE_LIMIT : body.code;

  let mensagem = MENSAGENS_ERRO[code] || body.message || mensagemPadrao;
  if (!error.response) {
    mensagem = 'Não foi possível conectar ao serviço de certidões. Verifique sua conexão e tente novamente.';
  }

  return { success: false, status, code, error: mensagem, data: body.data || null };
};

// Com responseType 'blob', o corpo de erro (JSON) também chega como Blob —
// converte de volta pra montarErro() conseguir ler `code` e `message`.
const lerErroBlob = async (error) => {
  const blob = error.response?.data;
  if (!(blob instanceof Blob)) return error;

  try {
    const body = JSON.parse(await blob.text());
    return { ...error, response: { ...error.response, data: body } };
  } catch (e) {
    return { ...error, response: { ...error.response, data: {} } };
  }
};

const extrairNomeArquivo = (contentDisposition) => {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition || '');
  return match ? decodeURIComponent(match[1]) : NOME_PDF_PADRAO;
};

const salvarArquivo = (blob, nome) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = nome;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const certidaoNadaConstaService = {
  async emitir() {
    try {
      const response = await certidaoApi.post('/certidoes/nada-consta/emitir');
      return { success: true, data: response.data?.data };
    } catch (error) {
      logger.error('Erro ao emitir Certidão Nada Consta:', error);
      return montarErro(error, 'Erro ao emitir a certidão.');
    }
  },

  async baixarPdf() {
    try {
      const response = await certidaoApi.get('/certidoes/nada-consta/pdf', { responseType: 'blob' });
      const nome = extrairNomeArquivo(response.headers['content-disposition']);
      salvarArquivo(response.data, nome);
      return { success: true };
    } catch (error) {
      logger.error('Erro ao baixar PDF da Certidão Nada Consta:', error);
      return montarErro(await lerErroBlob(error), 'Erro ao baixar o PDF da certidão.');
    }
  },

  async validar(codigo) {
    try {
      const response = await certidaoPublicaApi.get(`/certidoes/nada-consta/validar/${encodeURIComponent(codigo)}`);
      return { success: true, data: response.data?.data };
    } catch (error) {
      logger.error('Erro ao validar Certidão Nada Consta:', error);
      return montarErro(error, 'Erro ao consultar a certidão.');
    }
  }
};

// A API devolve as datas como 'YYYY-MM-DD' (sem hora). Formata por texto, sem
// new Date(), que interpretaria como UTC e mostraria o dia anterior no fuso
// de MS.
export const formatarDataCertidao = (data) => {
  const [ano, mes, dia] = String(data || '').slice(0, 10).split('-');
  return ano && mes && dia ? `${dia}/${mes}/${ano}` : '—';
};
